import type { ModuleDefinition } from "../../core/platform/registry/registry";
import type {
  SaleFinalizedEvent,
  SaleRefundedEvent,
  DrawerNoSaleAuthorizedEvent,
} from "../../core/domain/events/bus";

/**
 * Cash-drawer control.
 *
 * SCOPE BOUNDARY: this module records what the HARDWARE was asked to do and
 * whether it worked. It does NOT record the authorization — that is a core
 * cash-control fact written by core, whether or not this module exists
 * (FR-041b). The module never duplicates a reason or an authoriser; it
 * references the core record.
 *
 * THE CLOSED TRIGGER LIST IS THE SECURITY CONTROL. The drawer opens on exactly
 * three things and nothing else can reach the hardware, because no `kick()` is
 * exported from here. Future code cannot "just open the drawer" without adding
 * itself to this list, in this file, visibly.
 */

export type DrawerTrigger = "sale" | "refund" | "no_sale";

export type FailureCode =
  | "unsupported_browser"
  | "permission_lost"
  | "device_disconnected"
  | "kick_timeout";

export interface DrawerAttempt {
  id: string;
  trigger: DrawerTrigger;
  saleId: string | null;
  refundId: string | null;
  /** Points back at the CORE authorization record for a no-sale. */
  auditEntryId: string | null;
  staffId: string;
  method: "electronic" | "manual";
  outcome: "success" | "failed";
  failureCode: FailureCode | null;
  createdAt: string;
}

export interface DrawerHardware {
  isSupported(): boolean;
  /**
   * Has a drawer ever been paired on this terminal?
   *
   * Distinct from `isConnected`, and the distinction matters: a terminal that
   * HAS a drawer and cannot reach it has a problem worth interrupting someone
   * about, while a terminal that has never had one is simply a till without a
   * drawer. Conflating them makes every cash sale on a drawer-less terminal
   * raise a hardware failure for hardware that does not exist.
   */
  isConfigured(): boolean;
  isConnected(): boolean;
  kick(): Promise<void>;
}

export interface DrawerRecorder {
  record(attempt: Omit<DrawerAttempt, "id" | "createdAt">): Promise<DrawerAttempt>;
}

export interface DrawerPrompts {
  /** Asks staff to acknowledge that they opened the drawer by hand. */
  requestManualAcknowledgement(reason: FailureCode): Promise<boolean>;
}

export interface DrawerDeps {
  hardware: DrawerHardware;
  recorder: DrawerRecorder;
  prompts: DrawerPrompts;
}

/**
 * Decide whether an event should open the drawer.
 *
 * Exported for testing precisely because it IS the control. A change here is a
 * change to who can reach the cash, and it should be impossible to make one
 * without a test noticing.
 */
export function saleShouldOpenDrawer(event: { hasCashTender: boolean }): boolean {
  return event.hasCashTender;
}

export function refundShouldOpenDrawer(event: { hasCashRefund: boolean }): boolean {
  return event.hasCashRefund;
}

/**
 * Attempt a release and record what happened.
 *
 * Two properties, both non-negotiable:
 *
 *   A failed kick NEVER fails the sale (FR-102). The money is already taken;
 *   refusing to record it because a printer is unplugged would be strictly
 *   worse than an unopened drawer.
 *
 *   A failure is NEVER silent (FR-103). It writes electronic/failed, prompts
 *   for a manual acknowledgement, and that acknowledgement writes its own
 *   manual/success row. The pair is the audit trail for "the hardware was down
 *   and someone opened it with the key at 14:22" — the exact event that would
 *   otherwise be invisible, and silence is what a thief relies on.
 */
export async function attemptRelease(
  deps: DrawerDeps,
  context: {
    trigger: DrawerTrigger;
    staffId: string;
    saleId?: string | null;
    refundId?: string | null;
    auditEntryId?: string | null;
  },
): Promise<DrawerAttempt[]> {
  const base = {
    trigger: context.trigger,
    saleId: context.saleId ?? null,
    refundId: context.refundId ?? null,
    auditEntryId: context.auditEntryId ?? null,
    staffId: context.staffId,
  };

  /**
   * No drawer has ever been paired here, so there is nothing to open and
   * nothing has gone wrong. Say nothing.
   *
   * Without this, enabling the module on a terminal with no hardware would
   * raise a failed release and a manual-acknowledgement prompt on EVERY cash
   * sale — turning a feature nobody is using yet into an interruption in front
   * of a queue. The failure records exist to catch a drawer that should have
   * opened; a drawer that does not exist should not have.
   */
  if (!deps.hardware.isConfigured()) return [];

  const failure = detectFailure(deps.hardware);

  if (failure) {
    const failed = await deps.recorder.record({
      ...base,
      method: "electronic",
      outcome: "failed",
      failureCode: failure,
    });

    const acknowledged = await deps.prompts.requestManualAcknowledgement(failure);
    if (!acknowledged) return [failed];

    const manual = await deps.recorder.record({
      ...base,
      method: "manual",
      outcome: "success",
      failureCode: null,
    });
    return [failed, manual];
  }

  try {
    await deps.hardware.kick();
    return [
      await deps.recorder.record({
        ...base,
        method: "electronic",
        outcome: "success",
        failureCode: null,
      }),
    ];
  } catch {
    const failed = await deps.recorder.record({
      ...base,
      method: "electronic",
      outcome: "failed",
      failureCode: "kick_timeout",
    });

    const acknowledged = await deps.prompts.requestManualAcknowledgement("kick_timeout");
    if (!acknowledged) return [failed];

    return [
      failed,
      await deps.recorder.record({
        ...base,
        method: "manual",
        outcome: "success",
        failureCode: null,
      }),
    ];
  }
}

function detectFailure(hardware: DrawerHardware): FailureCode | null {
  if (!hardware.isSupported()) return "unsupported_browser";
  if (!hardware.isConnected()) return "device_disconnected";
  return null;
}

/**
 * The module definition.
 *
 * It subscribes to core events; core never calls it. Disabling it removes the
 * physical release and nothing else — the authorization record and its count in
 * the Z reading are core and survive (FR-041b).
 */
export function createDrawerModule(deps: DrawerDeps): ModuleDefinition {
  return {
    id: "peripherals",
    name: "Cash drawer",
    version: "0.1.0",

    subscribers: {
      "sale.finalized": async (event: SaleFinalizedEvent) => {
        if (!saleShouldOpenDrawer(event)) return;
        await attemptRelease(deps, {
          trigger: "sale",
          staffId: event.staffId,
          saleId: event.saleId,
        });
      },

      "sale.refunded": async (event: SaleRefundedEvent) => {
        if (!refundShouldOpenDrawer(event)) return;
        await attemptRelease(deps, {
          trigger: "refund",
          staffId: event.staffId,
          saleId: event.saleId,
          refundId: event.refundId,
        });
      },

      "drawer.noSaleAuthorized": async (event: DrawerNoSaleAuthorizedEvent) => {
        await attemptRelease(deps, {
          trigger: "no_sale",
          staffId: event.staffId,
          // References core's record rather than copying its reason/authoriser.
          auditEntryId: event.auditEntryId,
        });
      },
    },

    migrations: [{ id: "0005", file: "migrations/0005_module_peripherals.sql" }],
  };
}
